import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName, type SelectOption } from '@/nova/components';
import { MOBILE } from '@/nova/theme/tokens';

import * as S from './StudioSelect.styles';

export interface StudioSelectProps {
  /** Accessible label (i18n key); also rendered above the control unless `hideLabel`. */
  label: string;
  value: string;
  /** Options may carry an optional `description` node, rendered under the label inside the flyout (used by the
   *  video-model picker to explain each model); plain options render just their label as before. */
  options: (SelectOption & { description?: React.ReactNode })[];
  onChange: (value: string) => void;
  /** Shown in the trigger when `value` matches no option. */
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  /** Header/inline variant: label sits to the LEFT of a smaller trigger (same height as the header buttons). */
  compact?: boolean;
  /** Optional small glyph shown before the label. */
  iconName?: IconName;
  /** On a phone, the trigger is this glyph alone in a round button (the value shows in the open list). */
  mobileIcon?: IconName;
  /** Option labels (and `placeholder`) are shown as given, never through `t()`: data already in its own language,
   *  such as language endonyms. `label` is still translated. */
  rawLabels?: boolean;
}

/** The narrowest the options list gets when its trigger is just an icon, and its distance from the screen edges. */
const ICON_MENU_W = 340;
const ICON_MENU_GUTTER = 16;

type MenuPos = { left: number; width: number; top?: number; bottom?: number; maxHeight: number };

/**
 * A premium replacement for the studio editor's `<select>`s: the SAME props/look as the shared {@link Select},
 * but with a custom, rounded options flyout instead of the OS-native list. The flyout is PORTALED to <body> and
 * positioned as `position: fixed` off the trigger's screen rect, so it renders correctly even when the trigger
 * lives inside React Flow's transformed/zoomed canvas (an in-flow menu would be scaled and clipped). It opens up
 * or down depending on the room below, and closes on outside click, Escape, or any scroll/pan/resize.
 */
export const StudioSelect = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  hideLabel,
  compact,
  iconName,
  mobileIcon,
  rawLabels = false,
}: StudioSelectProps): React.ReactElement => {
  const { t } = useTranslation();
  const optionLabel = (text: string): string => (rawLabels ? text : t(text));
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<MenuPos | null>(null);
  const [active, setActive] = React.useState(0);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);

  const measure = React.useCallback((): void => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(360, openUp ? spaceAbove : spaceBelow));
    // An icon trigger is too narrow to size the list: give it a readable width, kept on screen.
    const iconOnly = Boolean(mobileIcon) && window.matchMedia(MOBILE.replace('@media ', '')).matches;
    const width = iconOnly ? Math.min(ICON_MENU_W, window.innerWidth - 2 * ICON_MENU_GUTTER) : r.width;
    const left = iconOnly
      ? Math.max(ICON_MENU_GUTTER, Math.min(r.left, window.innerWidth - width - ICON_MENU_GUTTER))
      : r.left;
    setPos({
      left,
      width,
      maxHeight,
      ...(openUp ? { bottom: window.innerHeight - r.top + gap } : { top: r.bottom + gap }),
    });
  }, [mobileIcon]);

  const openMenu = React.useCallback((): void => {
    if (disabled) return;
    measure();
    const i = options.findIndex((o) => o.value === value);
    setActive(i < 0 ? 0 : i);
    setOpen(true);
  }, [disabled, measure, options, value]);

  const choose = React.useCallback(
    (v: string): void => {
      // Re-picking the already-selected value is a no-op: skip onChange so a same-value click never fires the
      // consumer's edit handler (which, on a locked version, would fork a junk storyboard-only version). Just close.
      if (v !== value) onChange(v);
      setOpen(false);
    },
    [onChange, value],
  );

  // While open: close on an outside pointer, Escape, or ANY scroll / pan / resize (a fixed menu would otherwise
  // detach from its trigger). Bound only while open so it never leaks listeners.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Close on scroll / wheel (React Flow zooms via wheel, not scroll) / resize: a fixed menu would otherwise
    // drift away from its trigger when the canvas pans/zooms or the page scrolls. But NOT when the scroll/wheel
    // happens INSIDE the menu itself (a long options list scrolls in place).
    const onShift = (e: Event): void => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = (): void => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onShift, true);
    window.addEventListener('wheel', onShift, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onShift, true);
      window.removeEventListener('wheel', onShift, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const o = options[active];
      if (o) choose(o.value);
    }
  };

  return (
    <S.Root $compact={compact}>
      {hideLabel ? null : (
        <S.LabelRow>
          {iconName ? <Icon name={iconName} size={15} /> : null}
          <S.Label text={label} tag="span" />
        </S.LabelRow>
      )}
      <S.Trigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t(label)}
        disabled={disabled}
        $open={open}
        $compact={compact}
        $mobileIcon={Boolean(mobileIcon)}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        {mobileIcon ? (
          <S.MobileIcon>
            <Icon name={mobileIcon} size={18} />
          </S.MobileIcon>
        ) : null}
        <S.Value raw text={current ? optionLabel(current.label) : placeholder ? optionLabel(placeholder) : value} />
        <S.Chevron $open={open} viewBox="0 0 12 12" aria-hidden="true">
          <polyline points="2,4 6,8 10,4" strokeLinecap="round" strokeLinejoin="round" />
        </S.Chevron>
      </S.Trigger>
      {open && pos
        ? createPortal(
            <S.Menu
              ref={menuRef}
              role="listbox"
              aria-label={t(label)}
              style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
            >
              {options.map((o, i) => (
                <S.Option
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  $selected={o.value === value}
                  $active={i === active}
                  $rich={Boolean(o.description)}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(o.value)}
                >
                  <S.OptionText raw text={optionLabel(o.label)} />
                  {o.description ?? null}
                </S.Option>
              ))}
            </S.Menu>,
            document.body,
          )
        : null}
    </S.Root>
  );
};

export default StudioSelect;
